import LoadingSpinner from '@common/src/components/LoadingSpinner';
import useQueryParams from '@common/src/hooks/useQueryParams';
import ConfigureItemsGrid from '@flows/src/components/flows-configure/shared/ConfigureItemsGrid';
import ContentWrapper from '@flows/src/components/flows-configure/shared/ContentWrapper';
import { FLOWS_CONFIG_PATH } from '@flows/src/FlowsConfigRouter';
import { useFlowInfo } from '@flows/src/api/getFlowInfo';
import { useYellowhiveFlowsConfigureStore } from '@flows/src/store/zustand/flows-configure/yellowhiveFlowsConfigureStore';
import { useYellowhiveFlowsProducts } from '@flows/src/hooks/flows-configure/useYellowhiveFlowsProducts';
import { PartyType } from '@parties/src/types';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useParams } from 'react-router';
import { Card, HStack, Text } from '@wegroup/design-system';
import SelectAllButton from '@flows/src/components/flows-configure/shared/SelectAllButton';
import TableCheckbox from '@parties/src/features/overview/components/table/TableCheckbox';

const FlowsConfigureProducts: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const queryparams = useQueryParams();
  const partyType = (queryparams.get('type') as PartyType) || 'CUSTOMER';
  const { flowId } = useParams<{ flowId: string }>();

  const { data: flowInfo } = useFlowInfo(flowId || '');
  const isYellowhiveFlow = flowInfo?.sub_type === 'YELLOWHIVE';

  const [error, setError] = useState<string | undefined>(undefined);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

  // Get selected insurances and coverages from yellowhive store
  const insurances = useYellowhiveFlowsConfigureStore(
    (state) => state.insurances,
  );
  const coverages = useYellowhiveFlowsConfigureStore(
    (state) => state.coverages,
  );

  // Fetch products using the API
  const {
    data: productsData,
    isLoading,
    error: productsError,
  } = useYellowhiveFlowsProducts({
    insurance_codes: insurances.map((insurance) => insurance.code),
    coverage_codes: Object.values(coverages).flat(),
    partyType,
  });
  const products = productsData?.items || [];

  if (!flowId) {
    return (
      <Redirect
        to={{
          pathname: `${FLOWS_CONFIG_PATH}`,
          search: window.location.search,
        }}
      />
    );
  }

  // This component is only for yellowhive flows
  if (!flowInfo) {
    return <LoadingSpinner />;
  }

  if (!isYellowhiveFlow) {
    return (
      <Redirect
        to={{
          pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances`,
          search: window.location.search,
        }}
      />
    );
  }

  const handlePrevious = () => {
    // For yellowhive flows, go back to the last insurance's coverages
    const lastInsurance = insurances[insurances.length - 1];
    if (lastInsurance) {
      history.push({
        pathname: `${FLOWS_CONFIG_PATH}/${flowId}/insurances/${lastInsurance.code}/coverages`,
        search: window.location.search,
      });
    }
  };

  const handleNext = () => {
    // Validate that we have selections
    if (selectedProducts.length === 0) {
      setError(t('Please select at least one product'));
      return;
    }

    // For now, just go to finish
    // In the future, this could go to a quote generation or summary screen
    history.push({
      pathname: `${FLOWS_CONFIG_PATH}/${flowId}/finish`,
      search: window.location.search,
    });
  };

  const handleProductSelect = (productId: string) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    );
    if (error) setError(undefined);
  };

  // Show error from API if available
  if (productsError) {
    setError(t('Failed to fetch products'));
  }

  // Auto-select all products by default on first load
  const didInitRef = useRef(false);
  useEffect(() => {
    if (!didInitRef.current && products.length > 0) {
      setSelectedProducts(products.map((p) => p.id));
      didInitRef.current = true;
    }
  }, [products]);

  const isAllSelected =
    selectedProducts.length === products.length && products.length > 0;
  const handleSelectAll = () => setSelectedProducts(products.map((p) => p.id));
  const handleDeselectAll = () => setSelectedProducts([]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ContentWrapper
      onNext={handleNext}
      onPrevious={handlePrevious}
      question={t('CONVERSATIONS_REPORT-CREATION_PRODUCT-SELECTION')}
      error={error}
    >
      <ConfigureItemsGrid>
        {products.length > 1 ? (
          <SelectAllButton
            isAllSelected={isAllSelected}
            onDeselectAll={handleDeselectAll}
            onSelectAll={handleSelectAll}
          />
        ) : null}
        {products.map((product) => (
          <Card
            key={product.id}
            as="button"
            p="4"
            borderColor={
              error
                ? 'danger.main'
                : selectedProducts.includes(product.id)
                ? 'primary.500'
                : 'gray.50'
            }
            h="100%"
            onClick={() => handleProductSelect(product.id)}
            _hover={{
              borderColor: selectedProducts.includes(product.id)
                ? undefined
                : 'gray.100',
              boxShadow: error ? undefined : 'level2',
            }}
            cursor="pointer"
            transition="0.1s"
          >
            <HStack spacing="3" align="start">
              <div style={{ flexGrow: 1 }}>
                <Text fontWeight="medium" mb="1">
                  {product.name}
                </Text>
                <Text fontSize="sm" color="gray.600" mb="2">
                  {product.insurance.name}
                </Text>
              </div>
              <TableCheckbox
                isChecked={selectedProducts.includes(product.id)}
                onChange={() => null}
              />
            </HStack>
          </Card>
        ))}
      </ConfigureItemsGrid>
    </ContentWrapper>
  );
};

export default FlowsConfigureProducts;
